import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Subject, Observable } from 'rxjs';
import * as signalR from '@microsoft/signalr';

export interface MeetingSessionData {
  meetingId: string;
  meetingTitle: string;
  meetingData: any;
  startedBy: string;
  timestamp: string;
}

export interface MeetingMessage {
  userId: string;
  userName: string;
  message: string;
  timestamp: string;
}

export interface UnsealVoteEvent {
  meetingId: string;
  userId?: string;
  userName?: string;
  agendaIndex: number;
  bidId: string;
  documentScope: string;
  votedAt?: string;
}

export interface AgendaWorkflowActionEvent {
  meetingId: string;
  agendaIndex: number;
  action: { actionType: string; entityId: string; isApproved?: boolean; comments?: string; updatedAt?: string };
}

@Injectable({
  providedIn: 'root'
})
export class SignalRService {
  private hubConnection?: signalR.HubConnection;
  private connectionStartPromise?: Promise<void>;
  private connectionStatusSubject = new Subject<boolean>();
  public connectionStatus$ = this.connectionStatusSubject.asObservable();
  
  // Track meetings that user has joined (for reconnection)
  private joinedMeetings = new Map<string, { userId: string; userName: string }>();

  // Meeting session events
  private meetingSessionSubject = new Subject<MeetingSessionData>();
  public meetingSession$ = this.meetingSessionSubject.asObservable();

  // Meeting messages
  private meetingMessageSubject = new Subject<MeetingMessage>();
  public meetingMessage$ = this.meetingMessageSubject.asObservable();

  // User joined/left events
  private userJoinedSubject = new Subject<{ userId: string; userName: string }>();
  public userJoined$ = this.userJoinedSubject.asObservable();

  private userLeftSubject = new Subject<{ userId: string }>();
  public userLeft$ = this.userLeftSubject.asObservable();

  // User kicked events
  private userKickedSubject = new Subject<{ meetingId: string; userId: string; userName: string; kickedBy: string }>();
  public userKicked$ = this.userKickedSubject.asObservable();

  private userKickedFromMeetingSubject = new Subject<{ meetingId: string; kickedBy: string }>();
  public userKickedFromMeeting$ = this.userKickedFromMeetingSubject.asObservable();

  // Agenda navigation events (from admin)
  private agendaNavigationSubject = new Subject<{ meetingId: string; agendaIndex: number }>();
  public agendaNavigation$ = this.agendaNavigationSubject.asObservable();

  // Meeting attendees
  private meetingAttendeesSubject = new Subject<Array<{ userId: string; userName: string }>>();
  public meetingAttendees$ = this.meetingAttendeesSubject.asObservable();

  // Meeting ended event
  private meetingEndedSubject = new Subject<{ meetingId: string }>();
  public meetingEnded$ = this.meetingEndedSubject.asObservable();

  // Meeting status changed event
  private meetingStatusChangedSubject = new Subject<{ 
    meetingId: string; 
    meetingIdInt?: string;
    id?: number;
    status: string; 
    statusName: string;
    meetingTitle: string;
    members: any[];
    agendaItems: any[];
  }>();
  public meetingStatusChanged$ = this.meetingStatusChangedSubject.asObservable();

  // Meeting session state (sent when joining)
  private meetingSessionStateSubject = new Subject<{ meetingId: string; currentAgendaIndex: number; userAgendaData: any }>();
  public meetingSessionState$ = this.meetingSessionStateSubject.asObservable();

  // Agenda comment saved confirmation
  private agendaCommentSavedSubject = new Subject<{ meetingId: string; agendaIndex: number; comment: string }>();
  public agendaCommentSaved$ = this.agendaCommentSavedSubject.asObservable();

  // Agenda answer saved confirmation
  private agendaAnswerSavedSubject = new Subject<{ meetingId: string; agendaIndex: number; action: string; comment: string }>();
  public agendaAnswerSaved$ = this.agendaAnswerSavedSubject.asObservable();

  // User agenda data (for rejoining)
  private userAgendaDataSubject = new Subject<{ meetingId: string; currentAgendaIndex: number; userAgendaData: any }>();
  public userAgendaData$ = this.userAgendaDataSubject.asObservable();

  // Member answered agenda item
  private memberAnsweredAgendaSubject = new Subject<{ meetingId: string; userId: string; userName: string; agendaIndex: number; action: string; comment: string }>();
  public memberAnsweredAgenda$ = this.memberAnsweredAgendaSubject.asObservable();

  // All members answers for an agenda item
  private allMembersAnswersSubject = new Subject<{ meetingId: string; agendaIndex: number; answers: any[] }>();
  public allMembersAnswers$ = this.allMembersAnswersSubject.asObservable();

  // TC Decision updated event
  private tcDecisionUpdatedSubject = new Subject<{ meetingId: string; agendaIndex: number; tcDecisionEn: string; tcDecisionAr: string }>();
  public tcDecisionUpdated$ = this.tcDecisionUpdatedSubject.asObservable();
  
  // Observable for all TC Decisions (loaded from session)
  private allTCDecisionsSubject = new Subject<{ meetingId: string; decisions: any }>();
  public allTCDecisions$ = this.allTCDecisionsSubject.asObservable();

  // Members' answers cleared for an agenda (after broadcast TC decision)
  private membersAnswersClearedForAgendaSubject = new Subject<{ meetingId: string; agendaIndex: number }>();
  public membersAnswersClearedForAgenda$ = this.membersAnswersClearedForAgendaSubject.asObservable();

  private unsealVoteSavedSubject = new Subject<UnsealVoteEvent>();
  public unsealVoteSaved$ = this.unsealVoteSavedSubject.asObservable();

  private memberUnsealVotedSubject = new Subject<UnsealVoteEvent>();
  public memberUnsealVoted$ = this.memberUnsealVotedSubject.asObservable();

  private allUnsealVotesSubject = new Subject<{ meetingId: string; agendaIndex: number; votes: UnsealVoteEvent[] }>();
  public allUnsealVotes$ = this.allUnsealVotesSubject.asObservable();

  private bidDocumentsUnsealedSubject = new Subject<{ meetingId: string; agendaIndex: number; agendaItemId?: number; bidId: string; documentScope: string }>();
  public bidDocumentsUnsealed$ = this.bidDocumentsUnsealedSubject.asObservable();
  private agendaWorkflowActionUpdatedSubject = new Subject<AgendaWorkflowActionEvent>();
  public agendaWorkflowActionUpdated$ = this.agendaWorkflowActionUpdatedSubject.asObservable();
  private allAgendaWorkflowActionsSubject = new Subject<{ meetingId: string; agendaIndex: number; actions: any[] }>();
  public allAgendaWorkflowActions$ = this.allAgendaWorkflowActionsSubject.asObservable();

  constructor() {}

  // Start SignalR connection
  public async startConnection(): Promise<void> {
    // Check if backend is disabled
    if (environment.DISABLE_BACKEND) {
      console.warn('SignalR connection skipped: Backend is disabled in environment');
      return;
    }

    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    if (this.connectionStartPromise) {
      return this.connectionStartPromise;
    }

    const token = localStorage.getItem('QNBNToken');
    // SignalR uses its own HTTP/WebSocket client, so Angular HTTP interceptors
    // cannot attach the APIM subscription header. Pass the same subscription
    // key in APIM's supported query-string parameter instead.
    const hubUrl = `${environment.API_URL}/meetingHub?subscription-key=${encodeURIComponent(environment.APIM_SUBSCRIPTION_KEY)}`;
    
    console.log('Attempting to connect to SignalR hub at:', `${environment.API_URL}/meetingHub`);

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => {
          // Return the token (SignalR will add it as access_token query parameter)
          return token || '';
        },
        skipNegotiation: false,
        // APIM exposes the hub as a WebSocket API. Do not fall back to long
        // polling because HTTP and WebSocket APIs cannot share the same suffix.
        transport: signalR.HttpTransportType.WebSockets
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: retryContext => {
          if (retryContext.elapsedMilliseconds < 60000) {
            return 2000; // Retry after 2 seconds for the first minute
          }
          return 10000; // Retry after 10 seconds after the first minute
        }
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // Register event handlers
    this.registerHandlers();

    this.connectionStartPromise = this.hubConnection.start();

    try {
      await this.connectionStartPromise;
      console.log('SignalR connection started');
      this.connectionStatusSubject.next(true);
    } catch (error: any) {
      console.error('Error starting SignalR connection:', error);
      console.error('Connection URL:', `${environment.API_URL}/meetingHub`);
      console.error('Error details:', error.message || error);
      this.connectionStatusSubject.next(false);
      
      // Provide helpful error message
      if (error.message?.includes('ERR_CONNECTION_REFUSED') || error.message?.includes('Failed to fetch')) {
        console.warn('⚠️ Backend server might not be running. Please ensure the backend API is running on:', `${environment.API_URL}/meetingHub`);
      }
    }

    this.connectionStartPromise = undefined;

    // Handle connection state changes
    this.hubConnection.onclose((error) => {
      console.log('SignalR connection closed', error);
      this.connectionStatusSubject.next(false);
    });

    this.hubConnection.onreconnecting((error) => {
      console.log('SignalR reconnecting...', error);
      this.connectionStatusSubject.next(false);
    });

    this.hubConnection.onreconnected(async (connectionId) => {
      console.log('✅ SignalR reconnected with new ConnectionId:', connectionId);
      this.connectionStatusSubject.next(true);
      
      // Rejoin all meetings that user was part of before disconnection
      if (this.joinedMeetings.size > 0) {
        console.log(`🔄 Rejoining ${this.joinedMeetings.size} meeting(s) after reconnection...`);
        const rejoinPromises: Promise<void>[] = [];
        
        for (const [meetingId, userInfo] of this.joinedMeetings.entries()) {
          const rejoinPromise = this.hubConnection!.invoke('JoinMeeting', meetingId, userInfo.userId, userInfo.userName)
            .then(() => {
              console.log(`✅ Rejoined meeting: ${meetingId}`);
            })
            .catch((error) => {
              console.error(`❌ Failed to rejoin meeting ${meetingId}:`, error);
              // Remove from tracking if rejoin failed
              this.joinedMeetings.delete(meetingId);
            });
          rejoinPromises.push(rejoinPromise);
        }
        
        await Promise.allSettled(rejoinPromises);
        console.log('✅ Finished rejoining meetings after reconnection');
      }
    });
  }

  // Register SignalR event handlers
  private registerHandlers(): void {
    if (!this.hubConnection) return;

    // Handle meeting session open event
    this.hubConnection.on('OpenMeetingSession', (data: MeetingSessionData) => {
      console.log('OpenMeetingSession received:', data);
      this.meetingSessionSubject.next(data);
    });

    // Handle meeting messages
    this.hubConnection.on('ReceiveMessage', (data: MeetingMessage) => {
      console.log('ReceiveMessage received:', data);
      this.meetingMessageSubject.next(data);
    });

    // Handle user joined
    this.hubConnection.on('UserJoined', (data: { userId: string; userName: string }) => {
      console.log('UserJoined:', data);
      this.userJoinedSubject.next(data);
    });

    // Handle user left
    this.hubConnection.on('UserLeft', (data: { userId: string }) => {
      console.log('UserLeft:', data);
      this.userLeftSubject.next(data);
    });

    // Handle agenda navigation (from admin)
    this.hubConnection.on('AgendaNavigated', (data: { meetingId: string; agendaIndex: number }) => {
      console.log(`📥 AgendaNavigated event received: Meeting ${data.meetingId}, Agenda Index ${data.agendaIndex} (ConnectionId: ${this.hubConnection?.connectionId})`);
      this.agendaNavigationSubject.next(data);
    });

    // Handle meeting attendees update
    this.hubConnection.on('MeetingAttendees', (data: Array<{ userId: string; userName: string }>) => {
      console.log('MeetingAttendees:', data);
      this.meetingAttendeesSubject.next(data);
    });

    // Handle meeting ended event
    this.hubConnection.on('MeetingEnded', (data: { meetingId: string }) => {
      console.log('MeetingEnded:', data);
      this.meetingEndedSubject.next(data);
    });

    // Handle meeting status changed
    this.hubConnection.on('MeetingStatusChanged', (data: { 
      meetingId: string; 
      meetingIdInt?: string;
      id?: number;
      status: string; 
      statusName: string;
      meetingTitle: string;
      members: any[];
      agendaItems: any[];
    }) => {
      console.log('MeetingStatusChanged received:', data);
      this.meetingStatusChangedSubject.next(data);
    });

    // Handle meeting session state (sent when joining)
    this.hubConnection.on('MeetingSessionState', (data: { meetingId: string; currentAgendaIndex: number; userAgendaData: any }) => {
      console.log('MeetingSessionState received:', data);
      this.meetingSessionStateSubject.next(data);
    });

    // Handle agenda comment saved confirmation
    this.hubConnection.on('AgendaCommentSaved', (data: { meetingId: string; agendaIndex: number; comment: string }) => {
      console.log('AgendaCommentSaved:', data);
      this.agendaCommentSavedSubject.next(data);
    });

    // Handle agenda answer saved confirmation
    this.hubConnection.on('AgendaAnswerSaved', (data: { meetingId: string; agendaIndex: number; action: string; comment: string }) => {
      console.log('AgendaAnswerSaved:', data);
      this.agendaAnswerSavedSubject.next(data);
    });

    // Handle user agenda data (for rejoining)
    this.hubConnection.on('UserAgendaData', (data: { meetingId: string; currentAgendaIndex: number; userAgendaData: any }) => {
      console.log('UserAgendaData received:', data);
      this.userAgendaDataSubject.next(data);
    });

    // Handle member answered agenda item
    this.hubConnection.on('MemberAnsweredAgenda', (data: { meetingId: string; userId: string; userName: string; agendaIndex: number; action: string; comment: string }) => {
      console.log('MemberAnsweredAgenda received:', data);
      this.memberAnsweredAgendaSubject.next(data);
    });

    // Handle all members answers for an agenda item
    this.hubConnection.on('AllMembersAnswers', (data: { meetingId: string; agendaIndex: number; answers: any[] }) => {
      console.log('AllMembersAnswers received:', data);
      this.allMembersAnswersSubject.next(data);
    });

    // Handle user kicked event (for other attendees)
    this.hubConnection.on('UserKicked', (data: { meetingId: string; userId: string; userName: string; kickedBy: string }) => {
      console.log('UserKicked received:', data);
      this.userKickedSubject.next(data);
    });

    // Handle user kicked from meeting event (for the kicked user)
    this.hubConnection.on('UserKickedFromMeeting', (data: { meetingId: string; kickedBy: string }) => {
      console.log('UserKickedFromMeeting received:', data);
      this.userKickedFromMeetingSubject.next(data);
    });

    // Handle TC Decision updated event
    this.hubConnection.on('TCDecisionUpdated', (data: { meetingId: string; agendaIndex: number; tcDecisionEn: string; tcDecisionAr: string }) => {
      console.log('TCDecisionUpdated received:', data);
      this.tcDecisionUpdatedSubject.next(data);
    });

    // Handler for all TC Decisions (loaded from session)
    this.hubConnection.on('AllTCDecisions', (data: any) => {
      console.log('AllTCDecisions received from session:', data);
      this.allTCDecisionsSubject.next(data);
    });

    // Members' answers cleared for an agenda (after admin broadcasts TC decision)
    this.hubConnection.on('MembersAnswersClearedForAgenda', (data: { meetingId: string; agendaIndex: number }) => {
      console.log('[SignalRService] MembersAnswersClearedForAgenda received from hub:', data);
      console.log('[SignalRService] Broadcasting to subscribers...');
      this.membersAnswersClearedForAgendaSubject.next(data);
      console.log('[SignalRService] Broadcasted to subscribers');
    });

    this.hubConnection.on('UnsealVoteSaved', (data: UnsealVoteEvent) => {
      console.log('UnsealVoteSaved:', data);
      this.unsealVoteSavedSubject.next(data);
    });

    this.hubConnection.on('MemberUnsealVoted', (data: UnsealVoteEvent) => {
      console.log('MemberUnsealVoted:', data);
      this.memberUnsealVotedSubject.next(data);
    });

    this.hubConnection.on('AllUnsealVotes', (data: { meetingId: string; agendaIndex: number; votes: UnsealVoteEvent[] }) => {
      console.log('AllUnsealVotes:', data);
      this.allUnsealVotesSubject.next(data);
    });

    this.hubConnection.on('BidDocumentsUnsealed', (data: { meetingId: string; agendaIndex: number; agendaItemId?: number; bidId: string; documentScope: string }) => {
      console.log('BidDocumentsUnsealed:', data);
      this.bidDocumentsUnsealedSubject.next(data);
    });

    this.hubConnection.on('AgendaWorkflowActionUpdated', (data: AgendaWorkflowActionEvent) => this.agendaWorkflowActionUpdatedSubject.next(data));
    this.hubConnection.on('AllAgendaWorkflowActions', (data: { meetingId: string; agendaIndex: number; actions: any[] }) => this.allAgendaWorkflowActionsSubject.next(data));
  }

  // Join a meeting
  public async joinMeeting(meetingId: string, userId: string, userName: string): Promise<void> {
    // Track this meeting for reconnection
    this.joinedMeetings.set(meetingId, { userId, userName });
    
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      try {
        await this.hubConnection.invoke('JoinMeeting', meetingId, userId, userName);
        console.log(`✅ Successfully joined meeting: ${meetingId} (ConnectionId: ${this.hubConnection.connectionId})`);
      } catch (error) {
        console.error(`❌ Error joining meeting ${meetingId}:`, error);
        // Remove from tracking if join failed
        this.joinedMeetings.delete(meetingId);
        throw error;
      }
    } else {
      console.warn('SignalR connection not established. Starting connection...');
      await this.startConnection();
      // Retry join after connection is established - wait a bit for connection to stabilize
      await new Promise(resolve => setTimeout(resolve, 500));
      // Check if connection is now established using isConnected method
      if (this.isConnected()) {
        await this.joinMeeting(meetingId, userId, userName);
      } else {
        console.error('❌ Failed to establish SignalR connection for meeting join');
        this.joinedMeetings.delete(meetingId);
      }
    }
  }

  // Leave a meeting
  public async leaveMeeting(meetingId: string, userId: string): Promise<void> {
    // Remove from tracking
    this.joinedMeetings.delete(meetingId);
    
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      try {
        await this.hubConnection.invoke('LeaveMeeting', meetingId, userId);
        console.log(`✅ Left meeting: ${meetingId}`);
      } catch (error) {
        console.error('❌ Error leaving meeting:', error);
      }
    }
  }

  // Kick a user from meeting (admin/hoster only)
  public async kickUserFromMeeting(meetingId: string, userIdToKick: string, kickedByUserId: string): Promise<void> {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      try {
        await this.hubConnection.invoke('KickUserFromMeeting', meetingId, userIdToKick, kickedByUserId);
        console.log(`Kicked user ${userIdToKick} from meeting: ${meetingId}`);
      } catch (error) {
        console.error('Error kicking user from meeting:', error);
        throw error;
      }
    } else {
      throw new Error('SignalR connection not established');
    }
  }

  // Start a meeting session (triggers popup for all connected users)
  public async startMeetingSession(meetingId: string, meetingTitle: string, meetingData: any): Promise<void> {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      try {
        await this.hubConnection.invoke('StartMeetingSession', meetingId, meetingTitle, meetingData);
        console.log(`Started meeting session: ${meetingId}`);
      } catch (error) {
        console.error('Error starting meeting session:', error);
      }
    } else {
      console.warn('SignalR connection not established. Starting connection...');
      await this.startConnection();
      await this.startMeetingSession(meetingId, meetingTitle, meetingData);
    }
  }

  // Send message to meeting participants
  public async sendMessageToMeeting(meetingId: string, userId: string, userName: string, message: string): Promise<void> {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      try {
        await this.hubConnection.invoke('SendMessageToMeeting', meetingId, userId, userName, message);
      } catch (error) {
        console.error('Error sending message:', error);
      }
    }
  }

  // Stop SignalR connection
  public async stopConnection(): Promise<void> {
    if (this.hubConnection) {
      try {
        await this.hubConnection.stop();
        console.log('SignalR connection stopped');
        this.connectionStatusSubject.next(false);
      } catch (error) {
        console.error('Error stopping SignalR connection:', error);
      }
    }
  }

  // Navigate agenda (admin only - broadcasts to all users)
  public async navigateAgenda(meetingId: string, agendaIndex: number): Promise<void> {
    if (this.hubConnection?.state !== signalR.HubConnectionState.Connected) {
      console.warn(`SignalR connection is not established for agenda navigation. State: ${this.hubConnection?.state}. Starting connection...`);
      await this.startConnection();

      if (!this.isConnected()) {
        throw new Error('SignalR connection not established');
      }
    }
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      try {
        console.log(`📡 Broadcasting agenda navigation: Meeting ${meetingId}, Agenda Index ${agendaIndex} (ConnectionId: ${this.hubConnection.connectionId})`);
        await this.hubConnection.invoke('NavigateAgenda', meetingId, agendaIndex);
        console.log(`✅ Successfully broadcasted agenda navigation to agenda index: ${agendaIndex}`);
      } catch (error) {
        console.error(`❌ Error navigating agenda for meeting ${meetingId}, index ${agendaIndex}:`, error);
      }
    } else {
      console.error(`❌ Cannot navigate agenda: SignalR connection is not established. State: ${this.hubConnection?.state}`);
    }
  }

  // Get meeting attendees
  public async getMeetingAttendees(meetingId: string): Promise<void> {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      try {
        await this.hubConnection.invoke('GetMeetingAttendees', meetingId);
      } catch (error) {
        console.error('Error getting meeting attendees:', error);
      }
    }
  }

  // End meeting (admin only - broadcasts to all users)
  public async endMeeting(meetingId: string): Promise<void> {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      try {
        await this.hubConnection.invoke('EndMeeting', meetingId);
        console.log(`Ended meeting: ${meetingId} - Event broadcasted to all clients`);
      } catch (error) {
        console.error('Error ending meeting:', error);
        throw error;
      }
    } else {
      console.warn('SignalR connection not established. Cannot broadcast meeting ended event.');
    }
  }

  public async clearMeetingSession(meetingId: string): Promise<void> {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      try {
        await this.hubConnection.invoke('ClearMeetingSession', meetingId);
        console.log(`Cleared meeting session data for: ${meetingId}`);
      } catch (error) {
        console.error('Error clearing meeting session:', error);
        throw error;
      }
    } else {
      console.warn('SignalR connection not established. Cannot clear meeting session.');
    }
  }

  // Save agenda comment
  public async saveAgendaComment(meetingId: string, userId: string, agendaIndex: number, comment: string): Promise<void> {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      try {
        await this.hubConnection.invoke('SaveAgendaComment', meetingId, userId, agendaIndex, comment);
        console.log(`Saved agenda comment for agenda ${agendaIndex}`);
      } catch (error) {
        console.error('Error saving agenda comment:', error);
      }
    }
  }

  // Save agenda answer (accept/reject)
  public async saveAgendaAnswer(meetingId: string, userId: string, agendaIndex: number, action: string, comment: string): Promise<void> {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      try {
        await this.hubConnection.invoke('SaveAgendaAnswer', meetingId, userId, agendaIndex, action, comment);
        console.log(`Saved agenda answer: ${action} for agenda ${agendaIndex}`);
      } catch (error) {
        console.error('Error saving agenda answer:', error);
      }
    }
  }

  // Get user agenda data (for rejoining)
  public async getUserAgendaData(meetingId: string, userId: string): Promise<void> {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      try {
        await this.hubConnection.invoke('GetUserAgendaData', meetingId, userId);
        console.log(`Requested user agenda data for meeting ${meetingId}`);
      } catch (error) {
        console.error('Error getting user agenda data:', error);
      }
    }
  }

  // Get all members' answers for a specific agenda item
  public async getAllMembersAnswers(meetingId: string, agendaIndex: number): Promise<void> {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      try {
        await this.hubConnection.invoke('GetAllMembersAnswers', meetingId, agendaIndex);
        console.log(`Requested all members answers for agenda ${agendaIndex}`);
      } catch (error) {
        console.error('Error getting all members answers:', error);
      }
    }
  }

  // Update TC Decision for an agenda item (admin/secretary only)
  public async updateTCDecision(meetingId: string, agendaIndex: number, tcDecisionEn: string, tcDecisionAr: string): Promise<void> {
    console.log('[SignalRService] updateTCDecision called with:', { meetingId, agendaIndex });
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      try {
        console.log('[SignalRService] Invoking UpdateTCDecision on hub...');
        await this.hubConnection.invoke('UpdateTCDecision', meetingId, agendaIndex, tcDecisionEn, tcDecisionAr);
        console.log(`[SignalRService] UpdateTCDecision invoked successfully for meeting ${meetingId}, agenda ${agendaIndex}`);
      } catch (error) {
        console.error('[SignalRService] Error updating TC Decision:', error);
      }
    } else {
      console.error('[SignalRService] Cannot update TC Decision - hub not connected. State:', this.hubConnection?.state);
    }
  }

  // Get all TC Decisions from session (called when joining/refreshing)
  public async getAllTCDecisions(meetingId: string): Promise<void> {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      try {
        await this.hubConnection.invoke('GetAllTCDecisions', meetingId);
        console.log(`Requested all TC Decisions from session for meeting ${meetingId}`);
      } catch (error) {
        console.error('Error getting all TC Decisions:', error);
      }
    }
  }

  public async saveUnsealVote(meetingId: string, userId: string, agendaIndex: number, bidId: string, documentScope: string): Promise<void> {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      try {
        await this.hubConnection.invoke('SaveUnsealVote', meetingId, userId, agendaIndex, bidId, documentScope);
      } catch (error) {
        console.error('Error saving unseal vote:', error);
      }
    }
  }

  public async getAllUnsealVotes(meetingId: string, agendaIndex: number): Promise<void> {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      try {
        await this.hubConnection.invoke('GetAllUnsealVotes', meetingId, agendaIndex);
      } catch (error) {
        console.error('Error getting unseal votes:', error);
      }
    }
  }

  public async saveAgendaWorkflowAction(meetingId: string, agendaIndex: number, actionType: string, entityId: string, isApproved?: boolean, comments?: string): Promise<void> {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      await this.hubConnection.invoke('SaveAgendaWorkflowAction', meetingId, agendaIndex, actionType, entityId, isApproved ?? null, comments ?? null);
    }
  }

  public async getAgendaWorkflowActions(meetingId: string, agendaIndex: number): Promise<void> {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      await this.hubConnection.invoke('GetAgendaWorkflowActions', meetingId, agendaIndex);
    }
  }

  // Get connection state
  public isConnected(): boolean {
    return this.hubConnection?.state === signalR.HubConnectionState.Connected;
  }
}

